import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { v4 as uuidv4 } from "uuid";

import InternalCostShareResearchTeamRow
	from "components/BudgetBuilder/CostShare/InternalCostShare/InternalCostShareResearchTeamRow";
import { useEditingContext } from "components/BudgetBuilder/shared/EditingContext";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Subsection from "components/Subsection";
import Table from "components/Table";
import {
	saveBudgetBuilderForm,
	updateInternalCostShareLineItems,
	updateResearchTeamMembers,
} from "store/actions/budgetBuilder";
import { budgetBuilderSelectors } from "store/selectors";
import { getCostShareSubtotal, getFAAmountSubtotal, getPeriodSubtotal } from "utils/budgetBuilder";

import InternalCostShareButtons from "./InternalCostShareButtons";
import InternalCostShareRow from "./InternalCostShareRow";
import styles from "./styles.css";


const InternalCostShareList = () => {
	const faRateAmount = useSelector(budgetBuilderSelectors.currentPeriodRate);
	const { currencyFormatter } = useTenantFormatter();
	const { internalCostShare: [ isEditing, setIsEditing ] } = useEditingContext();
	const [ isModified, setIsModified ] = useState(false);
	const [ editedCategories, setEditedCategories ] = useState([]);
	const [ editedMembers, setEditedMembers ] = useState([]);
	const savedCategories = useSelector(budgetBuilderSelectors.internalCostShare);
	const savedMembers = useSelector(budgetBuilderSelectors.researchTeamMembers);
	const handleSetCategories = newCategories => {
		setEditedCategories(newCategories);
		setIsModified(true);
	};
	const handleSetMembers = newMembers => {
		setEditedMembers(newMembers);
		setIsModified(true);
	};

	useEffect(
		() => {
			setEditedCategories(savedCategories);
		},
		[ savedCategories ]
	);

	useEffect(
		() => {
			setEditedMembers(savedMembers);
		},
		[ savedMembers ]
	);

	const handleSave = () => {
		setIsEditing(false);
		updateInternalCostShareLineItems(editedCategories);
		updateResearchTeamMembers(editedMembers);
		saveBudgetBuilderForm();
		setIsModified(false);
	};


	const handleSyncRemoveItem = hash => {
		const updatedCategories = [ ...editedCategories.filter(member => member.hash !== hash) ];
		handleSetCategories(updatedCategories);
	};

	const addInternalCostShareItem = () => {
		let newCategories = [ ...editedCategories ];
		const newItem = {
			hash: uuidv4(),
			name: "",
			lineItem: "",
			requirement: "",
			unit: "",
			type: "",
			costShareAmount: 0,
			isFA: false,
			FA: faRateAmount,
			isResearchTeamMember: false,
		};
		newCategories.push(newItem);
		handleSetCategories(newCategories);
	};

	const faAmountSubtotal = useMemo(
		() => getFAAmountSubtotal(editedCategories, faRateAmount),
		[ editedCategories, faRateAmount ]
	);

	const costShareSubtotal = useMemo(
		() => getCostShareSubtotal(editedCategories) + getPeriodSubtotal(editedMembers, true),
		[ editedCategories, editedMembers ]
	);

	const handleReset = () => {
		setEditedCategories(savedCategories);
		setEditedMembers(savedMembers);
		setIsModified(false);
	};

	const handleUpdateInternalLineItem = (hash, update) => {
		let newCategories = [ ...editedCategories ];
		for (let i = 0; i < newCategories.length; i++) {
			let category = newCategories[i];
			if (category.hash === hash) {
				newCategories[i] = {
					...category,
					...update,
				};
				break;
			}
		}
		handleSetCategories(newCategories);
	};

	const handleMemberUpdate = (id, update) => {
		let newMembers = [ ...editedMembers ];
		for (let i = 0; i < newMembers.length; i++) {
			let member = newMembers[i];
			if (member.id === id) {
				newMembers[i] = {
					...member,
					...update,
				};
				break;
			}
		}
		handleSetMembers(newMembers);
	};

	const renderCategories = () => {
		let preSortCategories = editedCategories.sort((a, b) => (a.name > b.name) ? -1 : 1);

		return preSortCategories?.sort((a, b) => a.sort - b.sort).map((category, index) => (
			<Fragment key={index}>
				<InternalCostShareRow
					key={category.hash}
					category={category}
					isEditing={isEditing}
					handleUpdateInternalLineItem={handleUpdateInternalLineItem}
					handleSyncRemoveItem={handleSyncRemoveItem}
					position={index}
				/>
			</Fragment>
		));
	};

	return (
		<Subsection
			title={"Internal Cost Share"}
			buttons={InternalCostShareButtons({
				isEditing,
				isModified,
				setIsEditing,
				handleReset,
				handleSave,
				addInternalCostShareItem
			})}
		>
			<Table captionText="Internal Cost Share">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Line Item"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Requirement"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Unit"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Type"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"Cost Share Amount"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"Include F&A"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"Rate"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"F&A Amount"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"Cost Share Total"}
						</Table.HeaderCell>
						<Table.HeaderCell/>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{editedMembers?.map((member, index) => (
						<Fragment key={index}>
							<InternalCostShareResearchTeamRow
								category={member}
								isEditing={isEditing}
								handleMemberUpdate={handleMemberUpdate}
							/>
						</Fragment>
					))}
					{renderCategories()}
					<Table.Row className={`${styles.rowHeader} ${styles.row} ${styles.totalRowPadding}`}>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.totalCellPadding}`}>
							<span className={styles.bold}>{"Internal Subtotal"}</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={`${styles.bold} ${styles.cellAlignRight}`}>
								{currencyFormatter(faAmountSubtotal)}
							</span>
						</Table.Cell>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}>
							<span className={`${styles.bold} ${styles.cellAlignRight}`}>{currencyFormatter(costShareSubtotal)}</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}/>
					</Table.Row>
				</Table.Body>
			</Table>
		</Subsection>
	);
};

export default InternalCostShareList;
